<template>
  <router-link
    :to="{ name: routePathName, params: { inspectionId: inspection.id, scope: inspectionScope } }"
    class="inspection-card"
    activeClass="inspection-card--active"
  >
    <div class="inspection-card__body" ref="card">
      <div class="inspection-card__header">
        <p class="typo-ignore typo-small-title inspection-card__title">
          {{ inspection.inspected_by && inspection.inspected_by.full_name }}
          <span v-if="inspection.inspection_type" class="inspection-card__type">{{ inspectionTypeText }}</span>
        </p>
        <!-- Inspection status -->
        <p :class="`typo-ignore typo-small-title inspection-card__status inspection-card__status--${inspection.status}`">
          <PrEngBadge class="icon" v-if="inspection.professional_engineer" />
          <component :is="statusIcon[inspection.status]" />
        </p>
      </div>
      <!-- Short Id / Issue count -->
      <div class="inspection-card__info">
        <p class="typo-ignore typo-tiny inspection-card__id">
          #{{ inspection.id && shortId }}
          <span class="inspection-card__issues" v-if="inspection.issues">
            {{
              pluralize(
                inspection.issues.length,
                $t("activerecord.models.plants/issue.one"),
                $t("activerecord.models.plants/issue.other")
              )
            }}
          </span>
        </p>
        <p class="typo-ignore typo-tiny inspection-card__date">
          {{ formatDate(inspection.date) }}
        </p>
      </div>
      <!-- Issue dots, modified in colour using rating level -->
      <div
        v-if="!isShaft && inspection.issues"
        class="inspection-card__issue-summary"
      >
        <div
          v-for="(level, index) in riskRatingLevels"
          :key="index"
          :class="`inspection-card__issue inspection-card__issue--${level}`"
        >
      </div>
      </div>
      <!-- Issue dots, modified in colour using rating level -->
      <div
        v-if="isShaft && inspection.issues"
        class="inspection-card__issue-summary"
      >
        <div
          v-for="(rating, index) in conditionRatings"
          :key="index"
          :class="`inspection-card__issue inspection-card__issue--${rating}`"
        />
      </div>
    </div>
  </router-link>
</template>

<script setup>
import { computed, ref, watchEffect } from 'vue';
import I18n from 'i18n-js';
import { useRoute } from 'vue-router';

// Utils
import pluralize from '@/functions/pluralize';
import { renderShortId } from '@/functions/utils.js';
import getRiskRatingLevel from '@/functions/simm/get_risk_rating_level';
import { isInViewport } from '@/functions/utils';
import useOrganisationProfile from '@/functions/use_organisation_profile';
import { formatDate } from '@/functions/date_format_helpers';

// Icons
import PrEngBadge from '@/images/rosette_16.svg';
import CircleCheckmark16Svg from '@/images/circle-checkmark-16.svg';
import ViewFinderDot16Svg from '@/images/viewfinder-dot-16.svg';
import CircleDot16Svg from '@/images/circle-dotted-16.svg';
import { comparePriority } from '@/functions/priority';

const props = defineProps({
  inspection: { type: Object, required: true },
  pathName: { type: String, required: false, default: null },
});

/**
 * Status icons
 */
const statusIcon = {
  approved: CircleCheckmark16Svg,
  in_progress: ViewFinderDot16Svg,
  awaiting_approval: CircleDot16Svg,
};

const route = useRoute();
const { showRisk, inspectionTypeName } = useOrganisationProfile();

const card = ref(null);

const isShaft = props.pathName === 'shaftInspection';

/**
 * Returns I18n text for inspection types
 */
const inspectionTypeText = computed(() => {
  return inspectionTypeName(props.inspection.inspection_type);
});

const inspectionScope = computed(() => {
  return props.inspection.status === 'approved' ? 'approved_inspections' : 'inspections';
});

const shortId = computed(() => renderShortId(props.inspection.id));

const riskRatingLevels = computed(() => {
  if (isShaft || !props.inspection?.issues) return [];

  if (showRisk.value) {
    return props.inspection.issues
      .map((i) => i.risk_rating)
      .sort((a, b) => b - a)
      .map(getRiskRatingLevel);
  } else {
    return props.inspection.issues
      .map((i) => i.priority)
      .sort(comparePriority)
      .map((p) => `p${p}`);
  }
});

const conditionRatings = computed(() => {
  if (!isShaft || !props.inspection?.issues) return [];

  return props.inspection.issues
    .map((i) => i?.activity?.overall_condition_rating || i?.overall_condition_rating)
    .sort((a, b) => b - a);
});

const routePathName = computed(() => {
  return props.pathName || 'inspection';
});

watchEffect(() => {
  if (card.value && route.name === 'inspection' && route.params.inspectionId === props.inspection.id) {
    if (!isInViewport(card.value)) {
      card.value.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }
});
</script>
